<template>
  <section id="faq" class="faq" aria-labelledby="faq-title">
    <div class="container faq__layout">
      <div class="faq__intro">
        <p class="eyebrow">Allading, explained</p>
        <h2 id="faq-title">Good questions.<br />Clear answers.</h2>
        <p>
          Explore messaging, calls, privacy and backup, with a clear picture of
          how Allading works.
        </p>
        <a href="#download" class="faq__link"
          >Get Allading <span aria-hidden="true">↗</span></a
        >
      </div>
      <div class="faq__groups">
        <div
          v-for="(group, index) in faqGroups"
          :key="group.title"
          class="faq__group"
        >
          <h3>
            <span aria-hidden="true">{{
              String(index + 1).padStart(2, "0")
            }}</span
            >{{ group.title }}
          </h3>
          <details
            v-for="item in group.items"
            :key="item.question"
            class="faq__item"
          >
            <summary>
              {{ item.question
              }}<span class="faq__toggle" aria-hidden="true"></span>
            </summary>
            <p>{{ item.answer }}</p>
          </details>
        </div>
      </div>
    </div>
  </section>
</template>
<script setup lang="ts">
import { onMounted, onBeforeUnmount } from "vue";
import { faqGroups } from "@/content/faq";
let schema: HTMLScriptElement | undefined;
onMounted(() => {
  schema = document.createElement("script");
  schema.type = "application/ld+json";
  schema.id = "allading-faq-schema";
  schema.textContent = JSON.stringify({
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqGroups.flatMap((group) =>
      group.items.map((item) => ({
        "@type": "Question",
        name: item.question,
        acceptedAnswer: { "@type": "Answer", text: item.answer },
      }))
    ),
  });
  document.head.appendChild(schema);
});
onBeforeUnmount(() => schema?.remove());
</script>
<style scoped>
.faq {
  padding: var(--section-y) 0;
  background: #faf9fd;
}
.faq__layout {
  display: grid;
  grid-template-columns: 0.8fr 1.2fr;
  gap: 80px;
  align-items: start;
}
.faq__intro {
  position: sticky;
  top: calc(var(--nav-h) + 40px);
}
.faq h2 {
  font-family: var(--font-display);
  font-size: clamp(34px, 4vw, 56px);
  font-weight: 500;
  letter-spacing: -0.045em;
  line-height: 1.12;
  margin: 20px 0 24px;
}
.faq__intro > p:not(.eyebrow) {
  max-width: 360px;
  color: var(--text-secondary);
  font-size: 17px;
  line-height: 1.7;
}
.faq__link {
  display: inline-flex;
  gap: 24px;
  margin-top: 28px;
  padding: 8px 0;
  border-bottom: 1px solid #c6bedc;
  color: #6551a5;
  font-size: 14px;
}
.faq__group + .faq__group {
  margin-top: 48px;
}
.faq h3 {
  display: flex;
  gap: 18px;
  align-items: baseline;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.04em;
  margin-bottom: 18px;
}
.faq h3 span {
  color: #81729e;
  font-size: 11px;
  font-variant-numeric: tabular-nums;
}
.faq__item {
  border-top: 1px solid #dedbe9;
}
.faq__item:last-child {
  border-bottom: 1px solid #dedbe9;
}
.faq summary {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 24px;
  padding: 22px 0;
  cursor: pointer;
  list-style: none;
  font-size: 17px;
  font-weight: 500;
  line-height: 1.5;
}
.faq summary::-webkit-details-marker {
  display: none;
}
.faq summary:hover {
  color: #6551a5;
}
.faq summary:focus-visible,
.faq__link:focus-visible {
  outline: 2px solid #6551a5;
  outline-offset: 5px;
}
.faq__toggle {
  width: 14px;
  height: 14px;
  flex: 0 0 14px;
  position: relative;
}
.faq__toggle::before,
.faq__toggle::after {
  content: "";
  position: absolute;
  background: #6551a5;
}
.faq__toggle::before {
  width: 14px;
  height: 1px;
  top: 7px;
  left: 0;
}
.faq__toggle::after {
  width: 1px;
  height: 14px;
  top: 0;
  left: 7px;
}
.faq__item[open] .faq__toggle::after {
  display: none;
}
.faq__item > p {
  margin: 0;
  padding: 0 38px 24px 0;
  color: var(--text-secondary);
  font-size: 15px;
  line-height: 1.8;
}
@media (max-width: 1000px) {
  .faq__layout {
    gap: 40px;
    grid-template-columns: 0.7fr 1.3fr;
  }
}
@media (max-width: 767px) {
  .faq__layout {
    grid-template-columns: 1fr;
    gap: 40px;
  }
  .faq__intro {
    position: static;
  }
  .faq__intro > p:not(.eyebrow) {
    max-width: 520px;
  }
  .faq summary {
    font-size: 16px;
  }
  .faq__item > p {
    padding-right: 0;
  }
}
</style>
